import { PageHeader } from '@/components/layouts/PageHeader';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { getPipelineIcon } from '@/utils/constants';
import {
	Box,
	Button,
	CardContent,
	CircularProgress,
	Container,
	Skeleton,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DevJSONView from '@/components/DevJSONView';
import ClientNameFields from './Fields/ClientNameFields';
import ClientStatusFields from './Fields/ClientStatusFields';
import AddressFields from './Fields/AddressFields';
import ContactFields from './Fields/ContactFields';
import ClientCategoryFields from './Fields/ClientCategoryFields';
import CreationInfoFields from './Fields/CreationInfoFields';
import BankInfoFields from './Fields/BankInfoFields';
import LegalInfoFields from './Fields/LegalInfoFields';
import CategoryFields from './Fields/CategoryFields';
import ClientInfoFields from './Fields/ClientInfoFields';
import { useCreateNewClientMutation } from '@/features/clients/clientsApi';
import ClientSchemaProvider, { useClientSchemaContext } from '@/context/clientSchemaContext';
import { useGetFormConfigQuery } from '@/features/config/configApi';
import { FormikErrorScroll } from '@/hooks/useFormikHelper';
import { showToast } from '@/features/toast/toastSlice';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { paths } from '@/app/routes';
import { isProspectFieldVisible, VIEW_TYPES } from '@/features/config/helper';
import ClientClassificationFields from './Fields/ClientClassificationFields';
import { FolioDrawerHeader } from '@/components/Drawers/FolioDrawer/FolioDrawerHeader';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';
import { FolioDrawerFormGuard } from '@/components/Drawers/FolioDrawer/FolioDrawerFormGuard';
import { getClientFormError } from '@/utils/constants';
import { buildSupportTicketPrefill } from '@/components/dialogs/IssueReportingDialog/supportTicketPrefill';

const VIEW = VIEW_TYPES.create;

const CreateClientFormWrapper = ({ dialogView = false, onSubmit }) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const {
		isProspect,
		view,
		initialValues,
		schema: validationSchema,
		isLoading,
	} = useClientSchemaContext();

	const ParentComponent = dialogView ? Box : Container;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { getClientDriveId } = useClientStorageHooks(userDetails?.brokerGroupId);

	const fieldsQuery = useGetFormConfigQuery(
		{ configType: 'client' },
		{
			skip: !userDetails,
			selectFromResult: ({ data, ...rest }) => ({
				...rest,
				data:
					data?.filter((f) =>
						isProspect ? isProspectFieldVisible(f, view) : f.visible?.[view],
					) ?? [],
			}),
		},
	);

	const fields = useMemo(() => {
		return (fieldsQuery.data ?? []).slice().map((f) => ({
			...f,
			required: f.mandatory?.[isProspect ? 'prospect' : 'client'],
		}));
	}, [fieldsQuery.data, isProspect]);

	const [createClient, createClientState] = useCreateNewClientMutation();

	const PipelineIcon = useMemo(
		() =>
			getPipelineIcon(
				isProspect ? 'Prospects' : process.env.REACT_APP_PIPELINE_NEW_BUSINESS,
				dialogView ? '25px' : '40px',
				theme.palette.mode === 'dark' ? { bgColor: 'transparent' } : undefined,
			),
		[isProspect, dialogView, theme.palette.mode],
	);

	const title = `Create New ${isProspect ? 'Prospect' : 'Client'}`;

	return (
		<Formik
			{...{
				initialValues,
				validationSchema,
				validateOnBlur: true,
				enableReinitialize: true,
				onSubmit: async (values, { setFieldError, resetForm, setFieldTouched }) => {
					console.log('[onSubmit] Submitting values', values);
					const { contacts, ...otherValues } = values;

					const data = Object.keys(otherValues).reduce(
						(acc, val) => {
							const key = val.split('-')[0];

							const fieldData = fields.find((f) => f.name === key);
							acc[fieldData?.source ?? 'firebase'][val] = otherValues[val];
							if (val === 'SalesTeamId-obj') {
								acc['firebase']['salesTeamUserId'] = otherValues[val]?.id;
							}
							if (val === 'brokerGroupId-docId') {
								acc['firebase']['brokerGroupId'] = otherValues[val];
							}
							return acc;
						},
						{ insight: { contacts }, firebase: {} },
					);
					console.log('[onSubmit] Data going to create client', data);
					try {
						const res = await createClient(data).unwrap();
						console.log('[onSubmit] Create client res', res);
						try {
							await getClientDriveId(res.clientId);
						} catch (e) {
							console.error('Error getting client drive id', e);
						}

						dispatch(
							showToast({
								message: 'Created client!',
								action: {
									path: `${paths.clients}/${res.clientId}`,
									label: 'GO TO CLIENT',
								},
								autohide: false,
							}),
						);
						resetForm();
						onSubmit?.(res.clientId);
					} catch (e) {
						console.error('[onSubmit] Error creating client', e);
						const { message, formFieldName } = getClientFormError(e);

						dispatch(
							showToast({
								message: `Error creating client: ${message}`,
								error: true,
								autohide: false,
								action: {
									label: 'Report Issue',
									dialogName: 'issueReporting',
									data: buildSupportTicketPrefill({
										subject: 'Error creating client',
										errorMessage: message,
										areaImpacted: 'Clients',
									}),
								},
							}),
						);
						if (formFieldName) {
							try {
								console.log('[onSubmit] Field', formFieldName);
								setFieldTouched(formFieldName, true);
								setTimeout(() => {
									setFieldError(formFieldName, message);
								}, 100);
							} catch (err) {
								console.log('Could not set field error', err);
							}
						}
					}
				},
			}}
		>
			{({ isSubmitting, values, handleSubmit, touched, errors, validateForm }) => {
				const disabled = isSubmitting || createClientState.isLoading;

				return (
					<Form
						style={{
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							minHeight: 0,
						}}
					>
						{dialogView && <FolioDrawerHeader title={title} icon={PipelineIcon} />}
						<Box
							sx={{
								flex: 1,
								overflowY: 'auto',
								...(!dialogView && { paddingBottom: '2em' }),
							}}
						>
							<ParentComponent sx={{ pt: dialogView ? 0 : 3 }}>
								{!dialogView && <PageHeader title={title} icon={PipelineIcon} />}

								<FormikErrorScroll {...{ isSubmitting }} />
								<CardContent sx={dialogView ? { pt: 1 } : undefined}>
									<Typography variant='body1' mb='1.15em'>
										{`Fill out the following form to create your new ${
											isProspect ? 'prospect' : 'client'
										}.`}
									</Typography>

									{isLoading ||
									fieldsQuery.isLoading ||
									fieldsQuery.isUninitialized ? (
										<Skeleton width='100%' height='3em' />
									) : (
										<Stack direction='column' spacing={2}>
											<Typography variant='form_label'>
												Client details
											</Typography>
											<ClientStatusFields {...{ disabled }} />
											<ClientNameFields {...{ disabled }} />
											<ClientClassificationFields {...{ disabled }} />

											<AddressFields {...{ disabled }} />
											<ClientCategoryFields {...{ disabled }} />

											<ClientInfoFields {...{ disabled }} />
											<CreationInfoFields {...{ disabled }} />

											<LegalInfoFields {...{ disabled }} />
											<ContactFields {...{ disabled }} />
											<CategoryFields {...{ disabled }} />

											<BankInfoFields {...{ disabled }} />
										</Stack>
									)}
									<DevJSONView value={values} />
									<DevJSONView value={touched} />
									<DevJSONView value={errors} />
								</CardContent>
							</ParentComponent>
						</Box>
						<FolioDrawerFormGuard />
						<FolioDrawerFooter>
							<Button
								color='primary'
								onClick={async () => {
									handleSubmit();
									setTimeout(() => {
										validateForm();
									}, 100);
								}}
								variant='contained'
								disabled={isLoading || isSubmitting}
							>
								{isSubmitting ? (
									<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
								) : (
									'Submit'
								)}
							</Button>
						</FolioDrawerFooter>
					</Form>
				);
			}}
		</Formik>
	);
};

const CreateClientForm = ({ dialogView = false, onSubmit }) => {
	return (
		<ClientSchemaProvider {...{ view: VIEW }}>
			<CreateClientFormWrapper {...{ dialogView, onSubmit }} />
		</ClientSchemaProvider>
	);
};

export default CreateClientForm;
